package mapsearch

import apiclient.geoobjects.Content
import apiclient.geoobjects.MarkerColor
import apiclient.geoobjects.MarkerShape
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.mapsearch.MapMarker
import apiclient.mapsearch.MapMarkerResult
import apiclient.mapsearch.MarkerCluster
import apiclient.mapsearch.isArchived
import apiclient.mapsearch.isFlagged
import apiclient.tags.getTagValues
import apiclient.tags.getUniqueTag
import apiclient.tags.meetingAttendees
import apiclient.tags.taskState
import apiclient.validations.parseEnumValue
import auth.ApiUserStore
import com.github.nwillc.ksvg.RenderMode
import data.objects.ActiveObjectStore
import data.objects.views.CurrentMarkerClusterStore
import data.objects.views.cardinfo.getColorForOccupancy
import data.objects.views.cardinfo.getCustomFieldMap
import data.users.settings.LocalSettingsStore
import data.users.settings.zoneTypeColorMap
import data.users.settings.zoneTypeIconMap
import data.users.settings.zoneTypeShapeMap
import koin.koinCtx
import koin.withKoin
import kotlin.contracts.ExperimentalContracts
import kotlin.contracts.InvocationKind
import kotlin.contracts.contract
import kotlinx.browser.document
import kotlinx.html.DIV
import kotlinx.html.HtmlTagMarker
import kotlinx.html.TagConsumer
import kotlinx.html.attributesMapOf
import kotlinx.html.div
import kotlinx.html.dom.append
import kotlinx.html.img
import kotlinx.html.js.svg
import kotlinx.html.style
import kotlinx.html.visitAndFinalize
import maplibreGL.ClusterProperties
import maplibreGL.MaplibreMarkerOptions
import maplibreGL.Marker
import maplibreGL.toJs
import maplibreGL.toLngLat
import model.LocalSettings
import model.hideMarkerTitles
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import svgmarker.MarkerSize
import svgmarker.SvgIconOptions
import svgmarker.buildingSvgIconOptions
import svgmarker.eventSvgIconOptions
import svgmarker.makeClusterMarkerSvg
import svgmarker.makeSvgIcon
import svgmarker.taskSvgIconOptions
import svgmarker.zoneMarkerSvgIconOptions
import theme.FormationColors
import theme.FormationIcons
import theme.FormationShapes
import utils.getColor
import utils.getColorForStatus
import utils.getIcon
import utils.getShape
import utils.isBrightColor
import utils.resolveColor
import utils.resolveColorOrNull
import utils.resolveIcon
import utils.resolveIconOrNull
import utils.resolveShapeOrNull


@HtmlTagMarker
@OptIn(ExperimentalContracts::class)
inline fun TagConsumer<HTMLElement>.divWithId(
    classes: String? = null, id: String? = null, crossinline
    block: DIV.() -> Unit = {}
): HTMLDivElement {
    contract { callsInPlace(block, InvocationKind.EXACTLY_ONCE) }
    return DIV(attributesMapOf("class", classes, "id", id), this)
        .visitAndFinalize(this, block) as HTMLDivElement
}

val Number.clusterNumberLabel: String
    get() {
        val value = this.toDouble()
        return when {
            value >= 1_000_000 -> (value / 1_000_000).toInt().toString() + "1M+"
            value >= 100_000 -> "100K+"
            value >= 10_000 -> "10K+"
            value >= 5000 -> "5K+"
            value >= 1_000 -> "1K+"
            value >= 500 -> "500+"
            value >= 300 -> "300+"
            value >= 200 -> "200+"
            value >= 50 -> "50+"
            value >= 20 -> "20+"
            else -> value.toInt().toString()
        }
    }

fun MapMarkerResult.getSvgIconOptions(objectType: ObjectType? = null): SvgIconOptions {
    val marker = this

    // Use SvgIconOption class to gather all relevant info to create the Marker with
    return when (objectType) {
        ObjectType.Building -> {
            buildingSvgIconOptions(
                size = MarkerSize.S,
                flagged = marker.isFlagged,
                archived = marker.isArchived,
                desaturated = marker.isArchived,
                highlighted = false,
                bitmapImage = content?.filterIsInstance<Content.Icon>()?.firstOrNull()?.href,
            )
        }

        ObjectType.Task -> {
            taskSvgIconOptions(
                size = MarkerSize.S,
                flagged = marker.isFlagged,
                archived = marker.isArchived,
                desaturated = marker.isArchived,
                highlighted = false,
                stateColor = marker.tags.taskState?.getColor(),
                stateIcon = marker.tags.taskState?.getIcon(),
                bitmapImage = content?.filterIsInstance<Content.Icon>()?.firstOrNull()?.href,
            )
        }

        ObjectType.Event -> {
            withKoin {
                val meetingState = marker.tags.meetingAttendees?.firstOrNull {
                    it.userId == get<ApiUserStore>().current.userId
                }?.meetingInvitationStatus
                eventSvgIconOptions(
                    size = MarkerSize.S,
                    flagged = marker.isFlagged,
                    archived = marker.isArchived,
                    desaturated = marker.isArchived,
                    highlighted = false,
                    stateColor = meetingState?.getColor(),
                    stateIcon = meetingState?.getIcon(),
                    bitmapImage = content?.filterIsInstance<Content.Icon>()?.firstOrNull()?.href,
                )
            }
        }

        ObjectType.Zone -> {
            val customFields = getCustomFieldMap(marker.tags.getTagValues(ObjectTags.CustomField))
            val capacity = customFields["Capacity"]?.fieldValue?.toIntOrNull() ?: 0
            val occupancy =
                marker.tags.getTagValues(ObjectTags.ChildCount).getUniqueTag(ObjectType.ObjectMarker)?.toIntOrNull()
                    ?: 0
            val fraction =
                capacity.let { cap -> occupancy.let { occ -> if (cap > 0) occ.toDouble() / cap.toDouble() else -1.0 } } // -1.0 used to indicate unknown capacity
            val statusColor =
                parseEnumValue<MarkerColor>(marker.tags.getUniqueTag(ObjectTags.StatusColor)).getColorForStatus()
            val zoneType = customFields["Type"]?.fieldValue
            zoneMarkerSvgIconOptions(
                size = MarkerSize.S,
                color = (resolveColorOrNull(zoneTypeColorMap[customFields["Type"]?.fieldValue]) ?: resolveColorOrNull(
                    marker.color,
                ))
                    ?: FormationColors.White.color,
                icon = resolveIconOrNull(zoneTypeIconMap[customFields["Type"]?.fieldValue]) ?: resolveIcon(icon),
                shape = resolveShapeOrNull(zoneTypeShapeMap[customFields["Type"]?.fieldValue]) ?: resolveShapeOrNull(
                    shape,
                ) ?: FormationShapes.Diamond,
                flagged = marker.isFlagged,
                archived = marker.isArchived,
                desaturated = marker.isArchived,
                highlighted = false,
                stateColor = if (marker.isFlagged) {
                    FormationColors.RedError
                } else {
                    when (zoneType) {
                        "containers", "Container", "coils", "Coil" -> getColorForOccupancy(fraction)
                        "machine" -> statusColor
                        else -> null
                    }
                },
                bitmapImage = content?.filterIsInstance<Content.Icon>()?.firstOrNull()?.href,
            )
        }

        else -> SvgIconOptions(
            size = MarkerSize.S,
            icon = resolveIcon(marker.icon),
            profilePicture = null,
            bitmapPicture = content?.filterIsInstance<Content.Icon>()?.firstOrNull()?.href,
            bgShape = parseEnumValue<MarkerShape>(marker.shape).getShape(),
            iconColor = if (isBrightColor(resolveColor(marker.color))) {
                FormationColors.BlueDeep.color
            } else FormationColors.White.color,
            bgColor = resolveColor(marker.color),
            borderColor = null,
            iconOpacity = 1.0,
            bgOpacity = 1.0,
            borderOpacity = 1.0,
            hasNotification = false,
            // TODO insert highlighted stateColor and stateIcon
            stateColor = when {
//            highlighted -> FormationColors.MarkerYou
                marker.isFlagged -> FormationColors.RedError.color
                else -> when (objectType) {
                    ObjectType.Task -> marker.tags.taskState?.getColor()?.color
                    // TODO Add MeetingState -> color
                    else -> null
                }
            },
            stateIcon = when {
//            highlighted -> FormationUIIcons.LinkAlt
                marker.isFlagged -> FormationIcons.Caution
                else -> {
                    when (marker.objectType) {
                        ObjectType.Task -> marker.tags.taskState?.getIcon()
                        // TODO Add MeetingState -> icon
                        else -> null
                    }
                }
            },
            archived = marker.isArchived,
            flagged = marker.isFlagged,
            desaturated = marker.isArchived,
            highlighted = false,
        )
    }
}

/**
 * Get SvgIconOptions from MapMarker
 * with limited features, as we do not have the objectType here
 */
fun MapMarker.getSvgIconOptions(
    desaturated: Boolean = false,
): SvgIconOptions {
    val marker = this

    // Use SvgIconOption class to gather all relevant info to create the Marker with
    return SvgIconOptions(
        size = MarkerSize.S,
        icon = resolveIcon(marker.icon),
        profilePicture = null,
        bitmapPicture = content?.filterIsInstance<Content.Icon>()?.firstOrNull()?.href,
        bgShape = parseEnumValue<MarkerShape>(marker.shape).getShape(),
        iconColor = if (isBrightColor(resolveColor(marker.color))) {
            FormationColors.BlueDeep.color
        } else FormationColors.White.color,
        bgColor = resolveColor(marker.color),
        borderColor = null,
        iconOpacity = 1.0,
        bgOpacity = 1.0,
        borderOpacity = 1.0,
        hasNotification = false,
        // TODO insert highlighted or flagged stateColor and stateIcon
//            stateColor = when {
//                highlighted -> FormationColors.MarkerYou
//                flagged -> FormationColors.RedError
//                else -> stateColor
//            },
        // Hardcoded state icon & colors (for food and drink related markers at events)
        stateColor = if (this.keywords?.any { it in listOf("essen & trinken", "food & drinks") } == true) FormationColors.RedDarkCustom.color else null,
//            stateIcon = when {
//                highlighted -> FormationUIIcons.LinkAlt
//                flagged -> FormationIcons.Caution
//                else -> stateIcon
//            },
        stateIcon = if (this.keywords?.any { it in listOf("essen & trinken", "food & drinks") } == true) FormationIcons.Food else null,
        archived = false,
        flagged = false,
        desaturated = desaturated,
        highlighted = false,
    )
}

object MarkerGenerators {
    private val markerHolder by lazy {
        document.createElement("div") as HTMLElement
    }

    fun createClusterMarker(cluster: MarkerCluster, clusterId: String = "cl-${cluster.tilePath}"): Marker {
        val currentMarkerClusterStore: CurrentMarkerClusterStore by koinCtx.inject()
        val mapRenderOverrides: MapRenderOverrides by koinCtx.inject()

        val element = markerHolder.append {

            divWithId(
                classes = "cursor-pointer text-xs z-[1]",
                id = clusterId,
            ) {
                // TODO rewrite makeClusterMarkerSvg function to take "cluster.amount.label" as String, instead of "cluster.amount" as Integer
                svg { }.apply {
                    outerHTML = makeClusterMarkerSvg(
                        count = cluster.amount.toInt(),
                        // TODO remove number of markers later
                        amountLabel = cluster.amount.clusterNumberLabel, // + (cluster.markers?.size?.let { " ($it)" } ?: ""),
                        props = ClusterProperties(
                            objectTypes = emptyMap(),
                            colors = cluster.colorBreakDown.map { (color, number) ->
                                resolveColor(color) to number
                            }.toMap().ifEmpty { mapOf(FormationColors.White.color to cluster.amount.toInt()) },
                        ),
                        withShadow = true,
                        // TODO Set desaturation here when InsightsSuite is active
                        desaturated = mapRenderOverrides.pathToolActive.current,
                        cursorPointer = true,
                    )
                }
            }
        }.first()

        val markerOptions = MaplibreMarkerOptions(
            element = element.also { el ->
                if (!mapRenderOverrides.pathToolActive.current) {
                    el.addEventListener(
                        "click",
                        { event ->
                            event.stopPropagation()
                            event.stopImmediatePropagation()
                            currentMarkerClusterStore.onClusterClick(cluster)
                        },
                    )
                }
            },
        )
        return Marker(markerOptions.toJs()).also { marker ->
            marker.setLngLat(cluster.position.toLngLat())
        }
    }

    fun createMapLibreMarker(marker: MapMarker): Marker {
        val activeObjectStore: ActiveObjectStore by koinCtx.inject()
        val localSettingsStore: LocalSettingsStore by koinCtx.inject()
        val mapRenderOverrides: MapRenderOverrides by koinCtx.inject()

        val isActive: Boolean = activeObjectStore.current.id == marker.id
        val showTitle = !localSettingsStore.map(LocalSettings.hideMarkerTitles()).current
        val svgIconOptions = marker.getSvgIconOptions(
            desaturated = marker.id !in mapRenderOverrides.pathToolHighlightedObjectIds.current && mapRenderOverrides.pathToolActive.current,
        )

        val markerOptions = MaplibreMarkerOptions(
            element = makeSvgMarkerForMap(
                id = marker.id,
                title = marker.title,
                svgIconOptions = svgIconOptions,
                active = isActive,
                showTitle = showTitle,
            ).also { element ->
                // TODO: Unify these to one single event listener
                //  that looks up the object and then decides what to do,
                //  based on app state and marker type
                if (!svgIconOptions.desaturated) { // make non-clickable when desaturated (= disabled)
                    if (marker.id in mapRenderOverrides.pathToolHighlightedObjectIds.current && mapRenderOverrides.pathToolActive.current) {
                        element.addEventListener("click", pathToolTrackedObjectMarkerEventListener)
                    } else {
                        element.addEventListener("click", markerEventListener)
                    }
//                    if (objectType == ObjectType.HistoryEntry) {
//                        element.addEventListener("click", historyMarkerEventListener)
//                    } else if (objectType == ObjectType.ObjectMarker && marker.id in pathToolHighlightedObjectIds) {
//                        element.addEventListener("click", pathToolTrackedObjectMarkerEventListener)
//                    } else if (objectType == ObjectType.UserMarker || objectType == ObjectType.CurrentUserMarker) {
//                        element.addEventListener("click", userMarkerEventListener)
//                    } else {
//                        element.addEventListener("click", markerEventListener)
//                    }
                }
            },
        )
        val m = Marker(markerOptions.toJs())
        m.setLngLat(marker.position.toLngLat())
        return m
    }

    /**
     * Makes svg marker without using fritz2
     */
    fun makeSvgMarkerForMap(
        id: String,
        title: String? = null,
        svgIconOptions: SvgIconOptions,
        objectType: ObjectType = ObjectType.POI,
        active: Boolean = false,
        showTitle: Boolean = true,
        smallTitle: Boolean = false,
    ): HTMLElement {
        val objectId: String = id
        val titleId = "$objectId-marker-title"

        return markerHolder.append {
            divWithId(
                classes = "flex justify-center cursor-pointer " +
                    (if (active) "p-10 rounded-full bg-formationBlack/25 ring ring-white/25 " else "rounded-xl"),
                id = id,
            ) {
                style = (if (active) "z-index: 1; " else "z-index: unset; ") +
                    (if (svgIconOptions.desaturated) "filter: grayscale(100%); " else "")

                // MARKER ICON with user profile picture, bitmap picture or generated svg
                if (svgIconOptions.profilePicture != null) {
                    img(
                        classes = "w-10 max-h-10 object-cover cursor-pointer p-0.5 m-0.5 rounded-full shadow-xl " +
                            (if (active) "bg-highlight " else "bg-formationWhite ") +
                            (if (svgIconOptions.archived || svgIconOptions.flagged || svgIconOptions.desaturated) "opacity-50 " else ""),
                        src = svgIconOptions.profilePicture,
                    )
                } else if (svgIconOptions.bitmapPicture != null) {
                    div("relative") {
                        img(
                            classes = "max-w-18 max-h-10 object-scale-down cursor-pointer bg-formationWhite p-1.5 rounded-lg shadow-xl " +
                                (if (active) "ring ring-highlight " else "ring ring-transparent ") +
                                (if (svgIconOptions.archived || svgIconOptions.flagged || svgIconOptions.desaturated) "opacity-50 " else ""),
                            src = svgIconOptions.bitmapPicture,
                        )
                        // state icon
                        svgIconOptions.stateIcon?.let {
                            val stateColor = svgIconOptions.stateColor ?: FormationColors.White.color
                            div("absolute -top-1 -right-1 w-3 h-3") {
                                svg {}.apply {
                                    outerHTML = makeSvgIcon(
                                        svgIconOptions = SvgIconOptions(
                                            size = MarkerSize.XXXS,
                                            icon = svgIconOptions.stateIcon,
                                            iconColor = if (isBrightColor(stateColor)) FormationColors.BlueDeep.color else FormationColors.White.color,
                                            bgColor = stateColor,
                                            borderColor = FormationColors.White.color,
                                        ),
                                        renderMode = RenderMode.INLINE,
                                        debug = false,
                                        notificationIcon = FormationIcons.NotificationsOn,
                                    )
                                }
                            }
                        }
                    }
                } else {
                    divWithId(
                        classes = "rounded-xl " +
                            (if (svgIconOptions.archived || svgIconOptions.flagged || svgIconOptions.desaturated) "opacity-50 " else ""),
                    ) {
                        svg {}.apply {
                            outerHTML = makeSvgIcon(
                                svgIconOptions = svgIconOptions,
                                renderMode = RenderMode.INLINE,
                                debug = false,
                                notificationIcon = FormationIcons.NotificationsOn,
                            )
                        }
                    }
                }

                // MARKER TITLE with white text shadow
                if (active || (showTitle && svgIconOptions.bitmapPicture == null)) {
                    divWithId(
                        classes = "absolute flex items-center text-center w-max max-w-48 font-bold break-word " +
                            "text-shadow-markerTitle shadow-white " +
                            "mt-[var(--marker-margin-top)] " +
                            (if (svgIconOptions.flagged) "text-red-500 " else if (active) "text-highlight " else "") +
                            (if (svgIconOptions.archived || svgIconOptions.flagged || svgIconOptions.desaturated) "opacity-50 " else "") +
                            (if (smallTitle) "text-xs " else "text-sm "),
                        id = titleId,
                    ) {
                        // trick tailwindCSS by using var for calculated margin
                        style = "--marker-margin-top:${(svgIconOptions.size.smPixel * 1.15) + 1}px; " +
                            (if (svgIconOptions.desaturated) "filter: grayscale(100%);" else "")

                        title?.let { +it }
                    }

                } else {
                    // Do not render the title
                }
            }.apply {
                // EVENT LISTENERS
                if (!active) {
                    if ((showTitle && svgIconOptions.bitmapPicture == null)) {
                        if (!svgIconOptions.desaturated) {
                            if (objectType != ObjectType.GeneralMarker) {
                                onmouseenter = {
                                    (document.getElementById(objectId) as HTMLDivElement).style.zIndex = "1" // also above clusters (z = 1)
                                    (document.getElementById(titleId) as HTMLDivElement).style.zIndex = "1" // also above clusters (z = 1)
                                    Unit
                                }
                                onmouseleave = {
                                    (document.getElementById(objectId) as HTMLDivElement).style.zIndex = "unset"
                                    (document.getElementById(titleId) as HTMLDivElement).style.zIndex = "unset"
                                    Unit
                                }
                            }
                        }
                    } else {
                        // When flag showTitle == false:
                        // - Show marker title on mouse over
                        // - Remove it on mouse leave
                        onmouseenter = {
                            (document.getElementById(objectId) as HTMLDivElement).style.zIndex = "1"
                            this.append {
                                divWithId(
                                    classes = "absolute flex items-center text-center w-max max-w-48 font-bold break-word " +
                                        "text-shadow-markerTitle shadow-white " +
                                        "mt-[var(--marker-margin-top)] " +
                                        (if (svgIconOptions.flagged) "text-red-500 " else if (active) "text-highlight " else "") +
                                        (if (svgIconOptions.archived || svgIconOptions.flagged || svgIconOptions.desaturated) "opacity-50 " else "") +
                                        (if (smallTitle) "text-xs " else "text-sm "),
                                    id = titleId,
                                ) {
                                    // trick tailwindCSS by using var for calculated margin
                                    style = "--marker-margin-top:${(svgIconOptions.size.smPixel * 1.15) + 1}px; " +
                                        (if (svgIconOptions.desaturated) "filter: grayscale(100%);" else "")

                                    title?.let { +it } /*cutString(marker.title)*/
                                }
                            }

                        }
                        onmouseleave = {
                            document.getElementById(titleId)?.remove()
                            (document.getElementById(objectId) as HTMLDivElement).style.zIndex = "unset"
                            Unit
                        }
                    }
                }
            }
        }.first()
    }
}
